import { ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import ModuleInfo from 'Components/types/ModuleInfo.js';
import { SubgroupIcons, GroupIconMap, PowerSwitcherGroups } from 'Components/navigation/utils/navigationGroups.js';
import { camelCaseToKebabCase } from 'Js/utils/stringUtils';
import { NavigationIcons } from 'Components/icons/navigation/enIcons';
import { useUserSettingsStore } from 'Js/store/useUserSettingsStore';
import { ALL_MODULES } from './modules';
let tr = (_) => '';
const CACHE_KEY = 'accessibleModules';
/**
 * Normalizes a module template URL (containing @EngineServer / @EnPowerAccountId)
 * to a route-matchable path for comparison with vue-router route paths.
 * @param {string | undefined} url
 * @returns {string}
 */
export function normalizeModuleUrlToPath(url) {
    if (!url)
        return '';
    let path = url
        .replace('@EngineServer', '')
        .replace('@EnPowerAccountId', ':enPowerId');
    try {
        path = new URL(path, window.location.origin).pathname;
    }
    catch {
        path = path.split('?')[0].split('#')[0];
    }
    path = path.replace(/\/EnPower\/[^/]+/i, '/EnPower/:enPowerId');
    path = path.replace(/\/{2,}/g, '/');
    if (!path.startsWith('/'))
        path = `/${path}`;
    return path.length > 1 ? path.replace(/\/+$/, '') : path;
}
/**
 * Loads accessible modules from the global scope, caches in sessionStorage.
 * @returns {any[]}
 */
export function loadAccessibleModules() {
    return ALL_MODULES;
}
export default function useNavigationData(props, emit, localT) {
    const moduleGroups = ref([]);
    const openedEntryMap = ref({});
    const expandedChildGroups = ref(new Set());
    const router = useRouter();
    const route = useRoute();
    tr = localT ?? tr;
    /**
     * Deeply filters navigation groups and modules based on user permissions.
     * @param {any[]} groups
     * @param {string[]} permissions
     * @returns {any[]}
     */
    function filterModules(groups, permissions) {
        const { state: userSettingsState } = useUserSettingsStore();
        return groups
            .map(group => {
            const filteredGroup = { ...group };
            // Filter direct modules
            if (filteredGroup.ModuleConfigs) {
                filteredGroup.ModuleConfigs = filteredGroup.ModuleConfigs.filter((m) => {
                    const hasPermission = m.PermissionName ? permissions.includes(m.PermissionName) : true;
                    // EnflowEnloc module is a special case that doesn't have a PermissionName in ALL_MODULES but should be visible if permitted or by default if no permission is required
                    if (!hasPermission && m.Id === 'EnflowEnloc') {
                        return true;
                    }
                    if (m.DisabledInProd && !userSettingsState?.IsDev) {
                        return false;
                    }
                    return hasPermission;
                });
            }
            // Filter subgroups
            if (filteredGroup.ModuleConfigGroups) {
                filteredGroup.ModuleConfigGroups = filterModules(filteredGroup.ModuleConfigGroups, permissions);
            }
            return filteredGroup;
        })
            .filter(group => (group.ModuleConfigs && group.ModuleConfigs.length > 0) ||
            (group.ModuleConfigGroups && group.ModuleConfigGroups.length > 0));
    }
    /**
     * Load module data from the global scope.
     * @returns {boolean} True if data was loaded successfully, false otherwise.
     */
    function loadFromGlobal() {
        const { state: userSettingsState } = useUserSettingsStore();
        const permissions = userSettingsState?.ModulePermissions;
        if (permissions && Array.isArray(permissions)) {
            moduleGroups.value = filterModules(ALL_MODULES, permissions);
        }
        else {
            moduleGroups.value = ALL_MODULES;
        }
        return true;
    }
    /**
     * Closes all open navigation entries.
     */
    function closeMenu() {
        openedEntryMap.value = {};
    }
    /**
     * Load module data from the cache if not already loaded from global.
     * @returns {boolean} True if data was loaded successfully, false otherwise.
     */
    function loadFromCache() {
        if (moduleGroups.value.length)
            return true;
        try {
            const raw = sessionStorage.getItem(CACHE_KEY);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed) && parsed.length) {
                    moduleGroups.value = parsed;
                    return true;
                }
            }
        }
        catch { /* storage unavailable */ }
        return false;
    }
    const displayName = (item) => {
        return tr(`navigation.${item?.PermissionName}`) || item?.Area || item?.Name || 'Unnamed';
    };
    const moduleUrl = (module) => props.buildUrl(module.Url || '', props.currentEnpowerAccount);
    const hasContent = (group) => !!(group?.ModuleConfigGroups?.length || group?.ModuleConfigs?.length);
    /**
     * Build Ids for navigation items. (For test purposes)
     * @param {*} item
     * @returns
     */
    function buildId(item) {
        const name = (item && item.name) || '';
        return `navigation-link-${camelCaseToKebabCase(name).replace(' ', '')}`;
    }
    /**
     * Returns the DOM id for a navigation module element.
     * @param {*} module
     * @returns {string}
     */
    function moduleId(module) {
        const payload = { name: displayName(module) };
        if (props && typeof props.buildId === 'function')
            return props.buildId(payload);
        return buildId(payload);
    }
    /**
     * Shows module data.
     * @param {*} module
     * @param {*} parent
     * @returns
     */
    function moduleInfo(module, parent) {
        let iconClass;
        if (parent && parent.ModuleConfigs) {
            iconClass = subgroupIcon(parent);
        }
        else {
            iconClass = groupIcon(parent || module);
        }
        return new ModuleInfo({
            area: parent?.Area,
            name: module?.Name,
            icon: iconClass,
            url: module?.Url
        });
    }
    /**
     * Get a truncated display name for a module group when it exceeds a certain length (e.g. EN.Facts - Digitale Immobilienwirtschaft will be truncated to EN.Facts - Digitale Immobi…)
     * @param {*} item
     * @returns
     */
    function truncatedDisplayName(item) {
        const name = displayName(item);
        const limit = 18;
        return name.length > limit ? name.slice(0, limit) + '…' : name;
    }
    /**
     * Check if a module name should be truncated based on its length and specific group membership.
     * @param {*} item
     * @returns
     */
    function shouldTruncate(item) {
        const name = displayName(item);
        return PowerSwitcherGroups && Array.isArray(PowerSwitcherGroups) && PowerSwitcherGroups.includes(name);
    }
    /**
     * Handle click events on navigation items.
     * @param {*} module
     * @param {*} parent
     */
    function onClick(module, parent) {
        emit('update-recently-viewed-list', moduleInfo(module, parent));
    }
    /**
     * Get the key for a module group.
     * @param {*} group
     * @returns
     */
    function groupKey(group) {
        return group?.Id || group?.Area || group?.Name || displayName(group);
    }
    /**
     * Get the key for a child module.
     * @param {*} parentKey
     * @param {*} child
     * @returns
     */
    function childKey(parentKey, child) {
        return parentKey + '::' + (child?.Id || child?.Area || child?.Name || displayName(child));
    }
    /**
     * Check if a child group is expanded.
     * @param {*} key
     * @returns
     */
    function isChildExpanded(key) {
        return expandedChildGroups.value.has(key);
    }
    /**
     * Toggle the expanded state of a child group.
     * @param {*} key
     */
    function toggleChild(key) {
        if (expandedChildGroups.value.has(key))
            expandedChildGroups.value.delete(key);
        else
            expandedChildGroups.value.add(key);
        try {
            const arr = Array.from(expandedChildGroups?.value || []);
            sessionStorage.setItem('en_nav_expanded', JSON.stringify(arr));
        }
        catch (e) {
            console.error('Error saving expanded state from toggle:', e);
        }
    }
    /**
     * Get the icon class for a module subgroup.
     * @param {*} childGroup
     * @returns
     */
    function subgroupIcon(childGroup) {
        const name = displayName(childGroup);
        const mapped = SubgroupIcons[name];
        return mapped || NavigationIcons.OTHER;
    }
    /**
     * Get the icon class for a module group.
     * @param {*} group
     * @returns
     */
    function groupIcon(group) {
        const name = displayName(group);
        const mapped = GroupIconMap[name];
        if (!mapped)
            return 'mdi mdi-folder-outline';
        return mapped.startsWith('mdi') ? `mdi ${mapped}` : mapped;
    }
    /**
     * Check if a module is active based on the current route.
     * Highlight the active module in the navigation.
     * @param {*} module
     * @returns
     */
    function isModuleActive(module) {
        if (!module)
            return false;
        try {
            const currentPath = (route.path.replace(/\/+$/, '') || '/').toLowerCase();
            const target = document.createElement('a');
            target.href = moduleUrl(module);
            let modulePath = (target.pathname.replace(/\/+$/, '') || '/').toLowerCase();
            if (modulePath.length > 1 && modulePath.endsWith('/'))
                modulePath = modulePath.slice(0, -1);
            if (currentPath === '/' && modulePath === '/') {
                const name = displayName(module).toLowerCase();
                return name.includes('dashboard');
            }
            if (modulePath === currentPath)
                return true;
            return currentPath.startsWith(`${modulePath}/`) && router.resolve(modulePath).name === route.name;
        }
        catch {
            return false;
        }
    }
    /**
     * Try to resolve navigation from the SPA router; falls back to hard navigation.
     * @param {string} url
     */
    function tryNavigateTo(url) {
        closeMenu();
        if (!url)
            return;
        let targetUrl;
        try {
            targetUrl = new URL(url, window.location.origin);
        }
        catch {
            window.location.href = url;
            return;
        }
        if (targetUrl.origin !== window.location.origin) {
            window.location.href = targetUrl.href;
            return;
        }
        const routeUrl = `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
        const match = router.resolve(routeUrl);
        const matchedRoute = match.matched[0];
        const routeName = matchedRoute?.redirect ? null : matchedRoute?.name;
        const routeParams = match.params;
        if (routeName) {
            router.push({ name: routeName, params: routeParams, query: match.query, hash: targetUrl.hash });
        }
        else {
            window.location.href = targetUrl.href;
        }
    }
    return {
        CACHE_KEY,
        moduleGroups,
        openedEntryMap,
        closeMenu,
        expandedChildGroups,
        loadFromGlobal,
        loadFromCache,
        displayName,
        moduleUrl,
        moduleId,
        hasContent,
        moduleInfo,
        truncatedDisplayName,
        shouldTruncate,
        onClick,
        groupKey,
        childKey,
        isChildExpanded,
        toggleChild,
        subgroupIcon,
        groupIcon,
        isModuleActive,
        tryNavigateTo,
    };
}
